import React, { useState, useMemo, useRef } from 'react';

// --- Icons (Minimalist) ---
const Icons = {
    Filter: () => <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>,
    ExternalLink: () => <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
};

const launchpadGlassCardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(64px) saturate(200%)',
    WebkitBackdropFilter: 'blur(64px) saturate(200%)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(255, 255, 255, 0.04), 0 30px 86px rgba(0, 0, 0, 0.44)',
};

// --- Helper: Relative Time ---
const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
};

export default function LiveFeed({ issues, selectedLocation, setSelectedLocation, locations, onIssueClick }) {
    const [filterType, setFilterType] = useState('ALL'); // ALL, CRITICAL, WARNING
    const [searchTerm, setSearchTerm] = useState('');

    // --- Derived Data ---
    const filteredIssues = useMemo(() => {
        return issues.filter(issue => {
            // Location Filter
            if (selectedLocation !== 'All Locations' && issue.boardName !== selectedLocation) return false;

            // Search Filter
            if (searchTerm && !issue.locationName.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !issue.boardName.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Status Filter
            if (filterType === 'CRITICAL') return issue.status === 'Critical';
            if (filterType === 'WARNING') return issue.status === 'Needs Attention';

            return true;
        });
    }, [issues, selectedLocation, filterType, searchTerm]);

    const stats = useMemo(() => ({
        total: issues.length,
        critical: issues.filter(i => i.status === 'Critical').length,
        warning: issues.filter(i => i.status === 'Needs Attention').length
    }), [issues]);

    return (
        <div className="overview-launchpad-card h-full flex flex-col relative group overflow-hidden rounded-3xl" style={launchpadGlassCardStyle}>
            <div className="relative z-10 px-6 pt-6 pb-4 shrink-0">
                <div className="flex justify-center items-end mb-6">
                    <div className="relative inline-flex items-center">
                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="bg-transparent border border-white/10 rounded-full text-sm font-semibold text-white/80 tracking-widest uppercase font-sans focus:ring-0 focus:border-white/20 cursor-pointer text-center appearance-none pl-5 pr-9 py-2 hover:border-white/25 hover:text-white transition-all duration-200"
                            style={{ colorScheme: 'dark' }}
                        >
                            <option value="All Locations" style={{ background: '#1a1a1a', color: '#e5e5e5' }}>All Locations</option>
                            {locations.map(loc => <option key={loc} value={loc} style={{ background: '#1a1a1a', color: '#e5e5e5' }}>{loc}</option>)}
                        </select>
                        {/* Chevron indicator */}
                        <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                    </div>
                </div>

                {/* --- Control Deck (Minimalist) --- */}
                <div className="flex flex-col gap-4">
                    {/* Filter Tabs (Underline style) */}
                    <div className="flex justify-between px-12 border-b border-white/[0.04] pb-1 relative">
                        <FilterTab label="All Events" count={issues.length} active={filterType === 'ALL'} onClick={() => setFilterType('ALL')} />
                        <FilterTab label="Critical" count={stats.critical} active={filterType === 'CRITICAL'} onClick={() => setFilterType('CRITICAL')} color="red" />
                        <FilterTab label="Warnings" count={stats.warning} active={filterType === 'WARNING'} onClick={() => setFilterType('WARNING')} color="amber" />
                    </div>

                    {/* Search Bar */}
                    <div className="flex bg-[#0a0a0a] border border-white/5 rounded-lg p-0.5 relative group/search focus-within:border-white/10 transition-colors">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                placeholder="SEARCH"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-transparent border-none text-[11px] text-white placeholder-gray-700 py-1.5 pl-3 pr-2 focus:ring-0 tracking-wide font-sans uppercase font-medium h-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Feed List --- */}
            <div className="relative z-10 flex-1 overflow-hidden">
                <div className="absolute inset-0 overflow-y-scroll p-4 space-y-2 custom-scrollbar pr-2 pb-20">
                    {filteredIssues.length > 0 ? (
                        filteredIssues.map((issue) => (
                            <SpotlightCard
                                key={issue.id}
                                issue={issue}
                                onClick={() => onIssueClick && onIssueClick(issue.boardName)}
                            />
                        ))
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-800 opacity-40">
                            <div className="w-10 h-10 rounded-full border border-white/5 flex items-center justify-center mb-3">
                                <Icons.Filter />
                            </div>
                            <p className="text-[10px] tracking-widest uppercase">No Events Detected</p>
                        </div>
                    )}
                </div>
                {/* Fade Overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none"></div>
            </div>
        </div>
    );
}

// --- Sub-Components ---

const FilterTab = ({ label, count, active, onClick, color = 'white' }) => {
    const activeColor = {
        white: 'text-white border-white',
        red: 'text-red-500 border-red-500',
        amber: 'text-amber-500 border-amber-500'
    }[color];

    return (
        <button
            onClick={onClick}
            className={`
                pb-2 text-[10px] font-bold tracking-widest uppercase transition-all duration-300 border-b-2
                flex items-center gap-2
                ${active ? activeColor : 'text-[#333] border-transparent hover:text-gray-500'}
            `}
        >
            {label}
            <span className={`text-[9px] ${active ? 'opacity-100' : 'opacity-0'}`}>[{count}]</span>
        </button>
    );
};

const SpotlightCard = ({ issue, onClick }) => {
    const divRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [opacity, setOpacity] = useState(0);

    const handleMouseMove = (e) => {
        if (!divRef.current) return;
        const rect = divRef.current.getBoundingClientRect();
        setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const handleFocus = () => {
        setOpacity(1);
    };

    const handleBlur = () => {
        setOpacity(0);
    };

    const isCritical = issue.status === 'Critical';
    const isWarning = issue.status === 'Needs Attention';

    const getDiodeColor = () => {
        if (isCritical) return '#FF3B30';
        if (isWarning) return '#FFCC00';
        return '#34C759';
    };

    return (
        <div
            ref={divRef}
            onClick={onClick}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleFocus}
            onMouseLeave={handleBlur}
            className="relative px-4 py-4 bg-transparent border-b border-white/5 overflow-hidden group transition-colors duration-500 cursor-pointer"
        >

            {/* Content */}
            <div className="relative z-10">
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <div className="map-pin-diode transition-all duration-300 flex-shrink-0" style={{ backgroundColor: getDiodeColor() }}></div>
                        <span className="text-[9px] uppercase tracking-wider text-[#555] group-hover:text-gray-400 font-bold transition-colors">{getRelativeTime(issue.issueDate)}</span>
                    </div>
                    <a href={issue.mondayLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-600 hover:text-white transform translate-x-2 group-hover:translate-x-0 duration-300">
                        <Icons.ExternalLink />
                    </a>
                </div>

                <h4 className="text-[13px] font-bold text-gray-300 group-hover:text-white transition-colors mb-1 pr-4 leading-snug">
                    {issue.locationName}
                </h4>

                <p className="text-[10px] text-[#444] group-hover:text-gray-500 transition-colors uppercase tracking-wider font-medium">
                    {issue.boardName}
                </p>
            </div>
        </div>
    );
};
